// The evaluation card (design §8.1, rewritten for Phase 4.5).
//
// Shut, it is where the author stands: the goal, what is done, what is left,
// and how far along they are. Open, it is everything the advisor understands
// about the text as a whole — the subject, the vision, the style it is written
// in, who it is for, and what would count as success.
//
// This is the card that matters most, because every review reads it. The goal
// sentence is why the comments pull one way rather than several.
//
// Minutes spent are measured from the edit log; only the minutes remaining are
// the advisor's estimate.

import { Card, Reveal } from "./Card";
import { type Evaluation, score } from "./types";

interface Props {
  evaluation: Evaluation;
  onDismiss: () => void;
}

export function EvaluationCard({ evaluation, onDismiss }: Props) {
  const pct = score(evaluation);
  const met = evaluation.minutes_remaining === 0;

  return (
    <Card
      kind="evaluation"
      badge={<span className="kind-label">Where you stand</span>}
      onDismiss={onDismiss}
      summary={
        <>
          <ul className="progress-list">
            <li>
              <span className="progress-label">Goal:</span> {evaluation.goal}
            </li>
            <li>
              <span className="progress-label">Done:</span> {evaluation.done}
            </li>
            <li>
              <span className="progress-label">To-do:</span> {evaluation.todo}
            </li>
          </ul>
          <div
            className="progress-bar"
            role="progressbar"
            aria-valuenow={pct}
            aria-valuemin={0}
            aria-valuemax={100}
          >
            <div className="progress-fill" style={{ width: `${pct}%` }} />
          </div>
          <p className="progress-numbers">
            <span className="pct">{pct}%</span> · {minutes(evaluation.minutes_spent)} so far
            {met ? " · goal met" : ` · about ${minutes(evaluation.minutes_remaining)} to go`}
          </p>
        </>
      }
    >
      <dl className="four">
        <dt>Subject</dt>
        <dd>{evaluation.subject}</dd>
        <dt>Vision</dt>
        <dd>{evaluation.vision}</dd>
        <dt>Style</dt>
        <dd>{evaluation.style}</dd>
        <dt>Audience</dt>
        <dd>{evaluation.audience}</dd>
        <dt>Criteria</dt>
        <dd>{evaluation.criteria}</dd>
      </dl>
      {evaluation.reasoning && (
        <Reveal label="Reasoning">
          <p className="reasoning">{evaluation.reasoning}</p>
        </Reveal>
      )}
    </Card>
  );
}

function minutes(n: number): string {
  if (n < 60) return `${n} min`;
  const h = Math.floor(n / 60);
  const m = n % 60;
  return m === 0 ? `${h} h` : `${h} h ${m} min`;
}
